import React, { useEffect, useRef, useState } from 'react';
import EventAssignmentSettingsModal, { AssignmentAdmin } from '../../common/events/EventAssignmentSettingsModal';
import { listEventAdminAssignments, listEventAssignmentAdminCandidates, replaceEventAdminAssignments } from '../../../api/taskEvents';
import { useToast } from '../../common/ToastSystem';

export type AssignmentsSaved = (eventUuid: string, assignments: AssignmentAdmin[]) => void;
type Props = {
  eventUuid: string; adminId: number | string | null;
  onClose: () => void; onSaved?: AssignmentsSaved;
};
export default function EventListAssignmentModal({ eventUuid, adminId, onClose, onSaved }: Props) {
  const [draftIds, setDraftIds] = useState<number[]>([]);
  const [candidates, setCandidates] = useState<AssignmentAdmin[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [ready, setReady] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const active = useRef(false);
  const saveInFlight = useRef(false);
  const { success } = useToast() as { success: (message: string) => void };
  useEffect(() => {
    active.current = true;
    return () => { active.current = false; };
  }, []);
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setReady(false);
    setError('');
    Promise.all([listEventAdminAssignments(eventUuid), listEventAssignmentAdminCandidates()])
      .then(([current, available]) => {
        if (cancelled) return;
        if (!Array.isArray(current.assignments) || !Array.isArray(available.adminUsers)) {
          throw new Error('担当者情報を取得できませんでした。再読み込みしてください。');
        }
        const assigned = current.assignments as AssignmentAdmin[];
        setDraftIds(assigned.map(admin => Number(admin.admin_user_id ?? admin.id)).filter(id => Number.isInteger(id) && id > 0));
        const candidatesById = new Map<number, AssignmentAdmin>();
        [...assigned, ...available.adminUsers].forEach(admin => {
          const id = Number(admin.id ?? admin.admin_user_id);
          if (Number.isInteger(id) && id > 0) candidatesById.set(id, admin);
        });
        setCandidates([...candidatesById.values()]);
        setReady(true);
      }).catch(reason => {
        if (!cancelled) setError(reason?.message || '担当者情報の取得に失敗しました。');
      }).finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [eventUuid, reload]);
  const save = async () => {
    if (!ready || loading || saveInFlight.current) return;
    saveInFlight.current = true;
    setSaving(true);
    setError('');
    try {
      const response = await replaceEventAdminAssignments(eventUuid, { adminUserIds: draftIds });
      if (!active.current) return;
      if (!Array.isArray(response.assignments)) throw new Error('保存結果を確認できませんでした。再読み込みしてください。');
      success('担当者を更新しました');
      onSaved?.(eventUuid, response.assignments as AssignmentAdmin[]);
      onClose();
    } catch (reason) {
      if (active.current) setError((reason as Error)?.message || '担当者の更新に失敗しました。');
    } finally {
      saveInFlight.current = false;
      if (active.current) setSaving(false);
    }
  };
  const keyword = search.trim().toLowerCase();
  const filtered = candidates.filter(admin => [admin.display_name, admin.full_name, admin.nickname, admin.username, admin.department, admin.position]
    .filter(Boolean).join(' ').toLowerCase().includes(keyword));
  return <EventAssignmentSettingsModal open adminId={adminId} draftIds={draftIds} candidates={filtered}
    loading={loading} saving={saving} search={search} onSearch={setSearch}
    onToggle={id => setDraftIds(previous => previous.includes(id) ? previous.filter(value => value !== id) : [...previous, id])}
    onClose={() => { if (!saveInFlight.current) onClose(); }} onSave={save} error={error}
    onRetry={() => setReload(value => value + 1)} saveDisabled={!ready} />;
}
